import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const quickStats = [
  { label: 'Rast tržieb', value: '+312%', icon: 'ri-money-euro-circle-line', color: 'text-emerald-400' },
  { label: 'Organická návštevnosť', value: '+340%', icon: 'ri-user-received-2-line', color: 'text-orange-400' },
  { label: 'Konverzný pomer', value: '3.2×', icon: 'ri-shopping-cart-2-line', color: 'text-amber-400' },
  { label: 'ROI z kampaní', value: '820%', icon: 'ri-pie-chart-2-line', color: 'text-violet-400' },
];

const tags = ['WooCommerce', 'Google Ads', 'SEO', 'Meta Ads', 'E-mail Marketing', 'UI/UX Redesign'];

export default function CaseHero() {
  const { ref, inView } = useInView();

  return (
    <section className="relative min-h-screen flex flex-col justify-end pb-0 overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=professional%20cycling%20sports%20e-commerce%20website%20on%20large%20monitor%20in%20modern%20minimalist%20office%2C%20sleek%20white%20and%20black%20interface%20with%20high%20quality%20bicycle%20product%20photography%2C%20bright%20airy%20workspace%20with%20plants%2C%20natural%20light%20streaming%20through%20window%2C%20editorial%20photography%2C%20cinematic%20depth%20of%20field&width=1600&height=900&seq=cs-hero1&orientation=landscape"
          alt="SalenBike.sk case study"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-zinc-950/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/60 to-transparent" />
      </div>

      {/* Dotted grid */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '40px 40px' }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-40 pb-16 w-full">
        <div ref={ref} className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Badge row */}
          <div className="flex flex-wrap items-center gap-3 mb-8">
            <Link to="/" className="flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm transition-colors duration-200 cursor-pointer">
              <i className="ri-arrow-left-line text-xs" />
              Späť na referencie
            </Link>
            <span className="text-zinc-700">·</span>
            <span className="inline-flex items-center gap-1.5 bg-orange-500/15 border border-orange-500/30 text-orange-400 text-xs font-bold rounded-full px-3 py-1 uppercase tracking-wider">
              <i className="ri-trophy-line" />
              Case Study
            </span>
            <span className="inline-flex items-center gap-1.5 bg-zinc-800/80 border border-zinc-700/40 text-zinc-300 text-xs rounded-full px-3 py-1">
              <i className="ri-time-line text-zinc-500" />
              12-mesačná spolupráca
            </span>
            <span className="inline-flex items-center gap-1.5 bg-zinc-800/80 border border-zinc-700/40 text-zinc-300 text-xs rounded-full px-3 py-1">
              <i className="ri-map-pin-line text-zinc-500" />
              Slovensko
            </span>
          </div>

          {/* Title */}
          <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-none tracking-tight">
            SalenBike<span className="text-orange-400">.sk</span>
          </h1>
          <p className="text-zinc-300 text-xl md:text-2xl font-medium mb-4 max-w-3xl leading-relaxed">
            Ako sme pretransformovali lokálny cyklo e-shop na{' '}
            <span className="text-white font-bold">lídra trhu</span> s trojnásobným obratom
            za jediný rok.
          </p>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-12">
            {tags.map((tag) => (
              <span key={tag} className="text-xs text-zinc-400 bg-zinc-800/60 border border-zinc-700/40 rounded-lg px-3 py-1.5 font-medium">
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Quick stats strip */}
        <div
          className={`grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-800/40 border border-zinc-800/60 rounded-2xl overflow-hidden transition-all duration-700 delay-300 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {quickStats.map((stat, i) => (
            <div
              key={stat.label}
              className={`bg-zinc-900/90 backdrop-blur-sm px-6 py-6 flex flex-col gap-2 ${i < 3 ? 'border-r border-zinc-800/40' : ''}`}
            >
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${stat.icon} ${stat.color} text-base`} />
                </div>
                <span className="text-zinc-500 text-xs font-medium">{stat.label}</span>
              </div>
              <span className={`text-3xl font-black ${stat.color} leading-none`}>{stat.value}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
